import React from 'react';
import { Box, Text, Group, Stack, Paper, Badge } from '@mantine/core';
import dayjs from 'dayjs';
import type { DesignedShift } from '../types/prediction-types';

interface VerticalCalendarProps {
  shifts: DesignedShift[];
  startDate: Date;
  endDate: Date;
  skipped?: any[];
  onDateClick?: (date: string) => void;
}

interface DayData {
  date: string;
  dayName: string;
  dayNumber: number;
  patterns: {
    shift_type: string;
    shifts: DesignedShift[];
    totalRevenue: number;
  }[];
  isToday: boolean;
}

export default function VerticalCalendar({ shifts, startDate, endDate, skipped = [], onDateClick }: VerticalCalendarProps) {
  // Group shifts by date and shift_type (only selected patterns)
  const shiftsByDate: Record<string, Record<string, DesignedShift[]>> = {};
  
  shifts
    .filter(s => s.is_selected === 1)
    .forEach(shift => {
      if (!shiftsByDate[shift.date]) {
        shiftsByDate[shift.date] = {};
      }
      if (!shiftsByDate[shift.date][shift.shift_type]) {
        shiftsByDate[shift.date][shift.shift_type] = [];
      }
      shiftsByDate[shift.date][shift.shift_type].push(shift);
    });

  // Group skipped shifts by week
  const skippedByWeek: Record<string, any[]> = {};
  skipped.forEach(skip => {
    const date = dayjs(skip.date);
    const dayOfWeek = date.day(); // 0 = Sunday
    const weekStart = date.subtract(dayOfWeek, 'day').format('YYYY-MM-DD');
    
    if (!skippedByWeek[weekStart]) {
      skippedByWeek[weekStart] = [];
    }
    skippedByWeek[weekStart].push(skip);
  });

  // Generate all dates in range
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const allDays: DayData[] = [];
  
  let current = start;
  while (current.isBefore(end) || current.isSame(end, 'day')) {
    const dateStr = current.format('YYYY-MM-DD');
    const dayPatterns = shiftsByDate[dateStr] || {};
    
    const patterns = Object.entries(dayPatterns).map(([shift_type, dayShifts]) => {
      // Calculate total revenue for this pattern
      const patternRevenue = dayShifts.length > 0 ? dayShifts[0].pattern_revenue : 0;
      
      return {
        shift_type,
        shifts: dayShifts.sort((a, b) => a.shift_number - b.shift_number),
        totalRevenue: patternRevenue,
      };
    });

    allDays.push({
      date: dateStr,
      dayName: current.format('ddd'),
      dayNumber: current.date(),
      patterns,
      isToday: current.isSame(dayjs(), 'day'),
    });
    
    current = current.add(1, 'day');
  }

  // Group days into weeks (Sunday to Saturday)
  const weeks: DayData[][] = [];
  let currentWeek: DayData[] = [];
  
  // Add padding for first week if it doesn't start on Sunday
  if (allDays.length > 0) {
    const firstDayOfWeek = dayjs(allDays[0].date).day(); // 0 = Sunday, 1 = Monday, etc.
    if (firstDayOfWeek !== 0) {
      // Add empty padding cells for days before the first day
      for (let i = 0; i < firstDayOfWeek; i++) {
        currentWeek.push({
          date: '',
          dayName: '',
          dayNumber: 0,
          patterns: [],
          isToday: false,
        });
      }
    }
  }
  
  allDays.forEach((day, idx) => {
    currentWeek.push(day);
    
    // Start new week on Sunday (break on Saturday, day 6)
    if (dayjs(day.date).day() === 6 || idx === allDays.length - 1) {
      weeks.push([...currentWeek]);
      currentWeek = [];
    }
  });

  return (
    <Stack gap="lg">
      {weeks.map((week, weekIdx) => {
        // Filter out empty padding cells for week header
        const realDays = week.filter(d => d.date !== '');
        const weekStart = dayjs(realDays[0].date);
        const dayOfWeek = weekStart.day(); // 0 = Sunday
        const weekStartSunday = weekStart.subtract(dayOfWeek, 'day').format('YYYY-MM-DD');
        const weekSkipped = skippedByWeek[weekStartSunday] || [];
        
        return (
          <Box key={`week-${weekIdx}`}>
            {/* Week header */}
            <Text size="sm" fw={600} mb="xs" c="dimmed">
              {dayjs(realDays[0].date).format('MMM D')} - {dayjs(realDays[realDays.length - 1].date).format('MMM D, YYYY')}
            </Text>
            
            {/* Skipped shifts for this week */}
            {weekSkipped.length > 0 && (
              <Paper p="xs" mb="sm" withBorder bg="gray.0" style={{ borderLeft: '4px solid #fd7e14' }}>
                <Group gap="xs" align="center">
                  <Badge size="sm" color="orange" variant="light">Skipped</Badge>
                  <Text size="sm">
                    {weekSkipped.map((skip, idx) => {
                      const reasonLabels: Record<string, string> = {
                        'no_school_day': 'No School',
                        'holiday': 'Holiday',
                        'no_pattern': 'No Pattern'
                      };
                      
                      return (
                        <span key={idx}>
                          {idx > 0 && ', '}
                          <Text component="span" fw={500}>{dayjs(skip.date).format('dddd, MMM D, YYYY')}</Text>{' '}
                          <Text component="span" c="dimmed">{skip.shift_type}</Text>{' '}
                          <Text component="span" c="dimmed">({reasonLabels[skip.reason] || skip.reason})</Text>
                        </span>
                      );
                    })}
                  </Text>
                </Group>
              </Paper>
            )}
            
            {/* Week grid */}
            <Box
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: '8px',
              }}
            >
              {week.map((day, dayIdx) => {
                // Render empty cell for padding
                if (day.date === '') {
                  return <Box key={`empty-${dayIdx}`} />;
                }
                
                return (
                  <Paper
                    key={day.date}
                    p="sm"
                    withBorder
                style={{
                  cursor: day.patterns.length > 0 ? 'pointer' : 'default',
                  borderColor: day.isToday ? '#228be6' : undefined,
                  borderWidth: day.isToday ? '2px' : '1px',
                  minHeight: '180px',
                }}
                onClick={() => {
                  if (day.patterns.length > 0) {
                    onDateClick?.(day.date);
                  }
                }}
              >
                <Stack gap="xs">
                  {/* Day header */}
                  <Box>
                    <Text size="xs" c="dimmed" fw={500}>
                      {day.dayName}
                    </Text>
                    <Text size="lg" fw={day.isToday ? 700 : 600}>
                      {day.dayNumber}
                    </Text>
                  </Box>

                  {/* Shifts or empty state */}
                  {day.patterns.length > 0 ? (
                    <Box>
                      {/* Visual Timeline - One row per shift with overlap detection */}
                      <Box mb={6}>
                        {(() => {
                          // Combine all shifts from all patterns (morning + evening)
                          const allShifts = day.patterns.flatMap(p => p.shifts);
                          if (allShifts.length === 0) return null;
                          
                          // Helper: Convert time string to minutes
                          const timeToMinutes = (time: string) => {
                            const [h, m] = time.split(':').map(Number);
                            return h * 60 + m;
                          };
                          
                          // Helper: Format duration from minutes
                          const formatDuration = (minutes: number) => {
                            const hours = Math.floor(minutes / 60);
                            const mins = minutes % 60;
                            
                            if (hours === 0) {
                              return `${mins}m`; // Just minutes
                            }
                            
                            return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
                          };
                          
                          // Helper: Get color based on employee count IN THAT SHIFT
                          const getShiftColor = (employeeCount: number) => {
                            if (employeeCount === 1) {
                              return { bg: '#339af0', border: '#228be6' }; // Blue for 1 employee
                            } else {
                              return { bg: '#fa5252', border: '#f03e3e' }; // Red for 2+ employees
                            }
                          };
                          
                          // Sort shifts by start time
                          const sortedShifts = [...allShifts].sort((a, b) => {
                            return timeToMinutes(a.start_time) - timeToMinutes(b.start_time);
                          });
                          
                          // Find overall time range
                          const times = sortedShifts.flatMap(s => [
                            timeToMinutes(s.start_time),
                            timeToMinutes(s.end_time)
                          ]);
                          const minTime = Math.min(...times);
                          const maxTime = Math.max(...times);
                          const totalMinutes = maxTime - minTime;
                          
                          // Pre-calculate all overlaps for each shift
                          const shiftOverlaps = sortedShifts.map((shift, idx) => {
                            const shiftStart = timeToMinutes(shift.start_time);
                            const shiftEnd = timeToMinutes(shift.end_time);
                            const shiftDuration = shiftEnd - shiftStart;
                            
                            const overlaps: { start: number; end: number; withIndex: number }[] = [];
                            let overlapWithNextMinutes = 0;
                            
                            // Check overlaps with ALL other shifts
                            sortedShifts.forEach((otherShift, otherIdx) => {
                              if (otherIdx === idx) return;
                              
                              const otherStart = timeToMinutes(otherShift.start_time);
                              const otherEnd = timeToMinutes(otherShift.end_time);
                              
                              const overlapStart = Math.max(shiftStart, otherStart);
                              const overlapEnd = Math.min(shiftEnd, otherEnd);
                              
                              if (overlapStart < overlapEnd) {
                                overlaps.push({
                                  start: overlapStart,
                                  end: overlapEnd,
                                  withIndex: otherIdx
                                });
                                
                                // Track overlap with next shift specifically for text
                                if (otherIdx === idx + 1) {
                                  overlapWithNextMinutes = overlapEnd - overlapStart;
                                }
                              }
                            });
                            
                            return {
                              shift,
                              shiftStart,
                              shiftEnd,
                              shiftDuration,
                              overlaps,
                              overlapWithNextMinutes
                            };
                          });
                          
                          return (
                            <Box>
                              {/* Shared timeline background */}
                              <Box
                                style={{
                                  position: 'relative',
                                  height: `${shiftOverlaps.length * 18}px`,
                                  background: '#f8f9fa',
                                  borderRadius: '3px',
                                  marginBottom: '4px',
                                }}
                              >
                                {shiftOverlaps.map(({ shift, shiftStart, shiftEnd, overlaps }, idx) => {
                                  const leftPercent = ((shiftStart - minTime) / totalMinutes) * 100;
                                  const widthPercent = ((shiftEnd - shiftStart) / totalMinutes) * 100;
                                  const colors = getShiftColor(shift.employees);
                                  const topOffset = idx * 18;
                                  
                                  return (
                                    <React.Fragment key={idx}>
                                      {/* Main shift bar */}
                                      <Box
                                        style={{
                                          position: 'absolute',
                                          left: `${leftPercent}%`,
                                          width: `${widthPercent}%`,
                                          top: `${topOffset}px`,
                                          height: '16px',
                                          background: colors.bg,
                                          borderRadius: '2px',
                                          border: `1px solid ${colors.border}`,
                                        }}
                                      />
                                      
                                      {/* Show ALL overlaps with orange stripes */}
                                      {overlaps.map((overlap, oIdx) => {
                                        const overlapLeftPercent = ((overlap.start - minTime) / totalMinutes) * 100;
                                        const overlapWidthPercent = ((overlap.end - overlap.start) / totalMinutes) * 100;
                                        
                                        return (
                                          <Box
                                            key={`${idx}-${oIdx}`}
                                            style={{
                                              position: 'absolute',
                                              left: `${overlapLeftPercent}%`,
                                              width: `${overlapWidthPercent}%`,
                                              top: `${topOffset}px`,
                                              height: '16px',
                                              background: 'repeating-linear-gradient(45deg, rgba(255, 146, 43, 0.7), rgba(255, 146, 43, 0.7) 3px, transparent 3px, transparent 6px)',
                                              borderRadius: '2px',
                                              pointerEvents: 'none',
                                            }}
                                          />
                                        );
                                      })}
                                    </React.Fragment>
                                  );
                                })}
                              </Box>
                              
                              {/* Time and duration labels below the timeline */}
                              <Stack gap={2}>
                                {shiftOverlaps.map(({ shift, shiftDuration, overlapWithNextMinutes }, idx) => (
                                  <Text key={idx} size="10px" style={{ whiteSpace: 'nowrap', lineHeight: 1 }}>
                                    {shift.start_time}-{shift.end_time} ({formatDuration(shiftDuration)})
                                    {overlapWithNextMinutes > 0 && (
                                      <Text component="span" c="orange" fw={600}> ↓{formatDuration(overlapWithNextMinutes)}</Text>
                                    )}
                                  </Text>
                                ))}
                              </Stack>
                            </Box>
                          );
                        })()}
                      </Box>
                      
                      {/* Revenue only */}
                      <Text size="xs" fw={600} mt={4}>
                        ${day.patterns.reduce((sum, p) => sum + p.totalRevenue, 0).toFixed(0)}
                      </Text>
                    </Box>
                  ) : (
                    <Text size="xs" c="dimmed" ta="center" mt="md">
                      No games
                    </Text>
                  )}
                </Stack>
              </Paper>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
